import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TooltipModule } from 'primeng/tooltip';
import { Popover } from 'primeng/popover';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SettingsService } from '../../../../core/services/settings.service';
import { Store } from '@ngrx/store';
import { selectBranchId, selectCompanyId } from '../../../../core/store/auth/auth.selectors';
import { firstValueFrom, take } from 'rxjs';
import { FinancialService } from '../../../../core/services/financial.service';
import { toast } from 'ngx-sonner';
import { DtpEditModal } from './modals/dtp-edit-modal/dtp-edit-modal';
import { DtpProcedureModal } from './modals/dtp-procedure-modal/dtp-procedure-modal';
import { DtpDeleteModal } from './modals/dtp-delete-modal/dtp-delete-modal';
import { DtpImportRulesModal } from './modals/dtp-import-rules-modal/dtp-import-rules-modal';
import { DtpImportDiagnosticsModal } from './modals/dtp-import-diagnostics-modal/dtp-import-diagnostics-modal';
import { CataloguePaneComponent } from './catalogue-pane/catalogue-pane';
import { DtpLinkTreatmentModal } from './modals/dtp-link-treatment-modal/dtp-link-treatment-modal';
import { DtpLinkDiagnosisModal } from './modals/dtp-link-diagnosis-modal/dtp-link-diagnosis-modal';
import { AppButton } from '../../../../shared/components/app-button/app-button';
import { AuthService } from '../../../../core/services/auth.service';
import { DioService } from '../../../../core/services/dio.service';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import * as Ontology from '../../../../core/models/diagnocat-ontology';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { PermissionService, MODULE } from '../../../../core/services/permission.service';
import { DtpProcedureSettingsModal } from './modals/dtp-procedure-settings-modal';

/** Shared empties: a fresh [] on every change-detection pass makes child
 *  inputs look changed when nothing has. */
const EMPTY_NAMES: string[] = [];
const EMPTY_ENTRIES: any[] = [];

/** One subfamily and the diagnoses filed under it. */
export interface DiagnosticSubfamilyNode {
  key: string;
  label: string;
  items: any[];
}

/** One family: its subfamilies, plus any diagnosis that names the family but no
 *  subfamily and therefore hangs directly off it. */
export interface DiagnosticFamilyNode {
  key: string;
  label: string;
  /** True for the "Others" bucket, which sorts last. */
  isOthers: boolean;
  direct: any[];
  subfamilies: DiagnosticSubfamilyNode[];
  count: number;
}

@Component({
  selector: 'app-diagnostic-treatment-tab',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    DtpEditModal,
    DtpProcedureModal,
    DtpDeleteModal,
    AppButton,
    FormsModule,
    SelectModule,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    DtpImportRulesModal,
    DtpImportDiagnosticsModal,
    CataloguePaneComponent,
    DtpLinkTreatmentModal,
    DtpLinkDiagnosisModal,
    MenuModule,
    DtpProcedureSettingsModal,
    HasPermissionDirective,
    TooltipModule,
    Popover,
  ],
  templateUrl: './diagnostic-treatment-tab.html',
  styles: [
    `
      :host ::ng-deep {
        .p-select {
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          transition: all 0.2s !important;
        }
        .p-select:hover {
          border-color: var(--primary-color) !important;
        }
        .p-select .p-select-label {
          padding: 0 12px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #334155 !important;
        }
        .p-select .p-select-dropdown {
          width: 32px !important;
        }

        .custom-input {
          height: 32px !important;
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 10px !important;
          font-size: 11px !important;
          font-weight: 700 !important;
          color: #334155 !important;
          padding: 0 12px !important;
          transition: all 0.2s !important;
        }
        .custom-input:focus {
          border-color: var(--primary-color) !important;
          box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.1) !important;
          outline: none !important;
        }
      }
    `,
  ],
})
export class DiagnosticTreatmentTab implements OnInit {
  private settingsService = inject(SettingsService);
  private store = inject(Store);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private financialService = inject(FinancialService);
  private perm = inject(PermissionService);

  get canCreate(): boolean {
    return this.perm.canCreate(MODULE.CONFIGURACOES);
  }
  get canEdit(): boolean {
    return this.perm.canEdit(MODULE.CONFIGURACOES);
  }
  get canDelete(): boolean {
    return this.perm.canDelete(MODULE.CONFIGURACOES);
  }

  private dioService = inject(DioService);

  branchId: number | null = null;
  hasDiagnocatCreds = false;
  hasDioCreds = false;

  diagnostics: any[] = [];
  treatments: any[] = [];
  procedures: any[] = [];

  // System rows are gated by the provider they belong to (via the ontology FK),
  // so a Diagnocat-only clinic never sees Dio's Portuguese catalog and vice versa.
  // Rows with neither FK fall back to the Diagnocat gate (legacy behaviour).
  private providerVisible = (item: any): boolean => {
    if (!item.is_System_Generated) return true;
    if (item.dio_ontology_rule_id) return this.hasDioCreds;
    return this.hasDiagnocatCreds;
  };

  /** One job at a time: the clinic's own catalogue, or the provider mapping.
   *  Rationale in docs/DIO_INTEGRATION.md, "Settings > Diagnostics & Treatments". */
  view: 'catalogue' | 'mapping' = 'catalogue';
  /** Which provider's entries the mapping view lists. */
  mappingProvider: 'dio' | 'diagnocat' = 'dio';
  mappingSearch = '';
  mappingFilter: 'all' | 'linked' | 'unlinked' = 'all';

  diagnosticSearch = '';

  /** Catalogue presentation: the flat list, or grouped by family > subfamily. */
  diagnosticsView: 'list' | 'tree' = 'list';

  /**
   * What the Treatments column is showing.
   *
   * 'diagnostic' - the treatments mapped to the selected diagnosis (the
   *   original behaviour, and still the default).
   * 'all'        - every treatment in the branch, so the catalogue can be
   *   browsed without first guessing which diagnosis a treatment sits under.
   *
   * Selecting a diagnosis always returns to 'diagnostic'.
   */
  treatmentsScope: 'diagnostic' | 'all' = 'diagnostic';

  /** Every column searches now. Previously only Diagnostics did, which left up
   *  to 884 procedures and the whole treatment list browse-only. */
  treatmentSearch = '';
  procedureSearch = '';

  /**
   * Which column is on screen below md.
   *
   * Three stacked 720px panels meant 2160px of scrolling, and picking a
   * diagnosis updated a panel a screen and a half below the fold - so it looked
   * like nothing happened. Below md this becomes a drill-down: one column at a
   * time, with a back bar. At md and up all three show as before.
   */
  mobilePane: 'diagnostics' | 'treatments' | 'procedures' = 'diagnostics';

  paneClass(pane: 'diagnostics' | 'treatments' | 'procedures'): string {
    // Deterministic: exactly one display utility applies per breakpoint, so
    // there is no hidden-vs-flex ordering fight in the generated CSS.
    return this.mobilePane === pane ? 'flex flex-col min-h-0' : 'hidden md:flex md:flex-col min-h-0';
  }

  mobileBack() {
    this.mobilePane = this.mobilePane === 'procedures' ? 'treatments' : 'diagnostics';
  }

  /** Groups the user has explicitly opened. Everything starts closed: a branch
   *  can carry several hundred diagnoses and opening them all defeats the point
   *  of grouping. A search overrides this - see isGroupOpen. */
  private openGroups = new Set<string>();

  /** The tree is rebuilt only when the catalogue or the search actually change.
   *  visibleDiagnostics is a getter that Angular re-runs every change-detection
   *  pass, and regrouping 400+ rows that often is wasted work. */
  private treeCache: { key: string; tree: DiagnosticFamilyNode[] } | null = null;
  private diagnosticsRevision = 0;

  get showMappingView(): boolean {
    return this.hasDiagnocatCreds || this.hasDioCreds;
  }

  setView(view: 'catalogue' | 'mapping') {
    this.view = view;
    if (view === 'mapping' && !this.providerConfigured(this.mappingProvider)) {
      this.mappingProvider = this.mappingProvider === 'dio' ? 'diagnocat' : 'dio';
    }
    this.cdr.detectChanges();
  }

  providerConfigured(p: 'dio' | 'diagnocat'): boolean {
    return p === 'dio' ? this.hasDioCreds : this.hasDiagnocatCreds;
  }

  /** Credentials arrive asynchronously, so the default provider is corrected
   *  once they land rather than only when the mapping view is opened - the
   *  badge on the switch is visible before that. */
  private settleMappingProvider() {
    if (!this.providerConfigured(this.mappingProvider) && this.showMappingView) {
      this.mappingProvider = this.hasDioCreds ? 'dio' : 'diagnocat';
    }
  }

  setMappingProvider(p: 'dio' | 'diagnocat') {
    this.mappingProvider = p;
    this.cdr.detectChanges();
  }

  /** The chosen provider's entries, one row per distinct name. Write every
   *  twin, count one concept: see docs/DIO_INTEGRATION.md, "Same-named
   *  duplicates". */
  private get providerEntries(): any[] {
    // A provider the clinic has no credentials for has no entries here: the
    // catalogue rows exist for every clinic, so without this the progress
    // badge reported another provider's totals.
    if (!this.providerConfigured(this.mappingProvider)) return EMPTY_ENTRIES;
    const byName = new Map<string, any>();
    for (const d of this.diagnostics) {
      if (!d.is_System_Generated) continue;
      if (this.isDioItem(d) !== (this.mappingProvider === 'dio')) continue;
      const key = String(d.name ?? '')
        .trim()
        .toLowerCase();
      // Prefer an already-linked twin so the row shows the link that exists.
      if (
        !byName.has(key) ||
        (!this.linkedMasterName(byName.get(key)) && this.linkedMasterName(d))
      ) {
        byName.set(key, d);
      }
    }
    return [...byName.values()].sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }

  get mappingRows(): any[] {
    const s = this.mappingSearch.trim().toLowerCase();
    return this.providerEntries
      .filter((d) =>
        this.mappingFilter === 'all'
          ? true
          : this.mappingFilter === 'linked'
          ? !!this.linkedMasterName(d)
          : !this.linkedMasterName(d)
      )
      .filter(
        (d) =>
          !s ||
          String(d.name ?? '')
            .toLowerCase()
            .includes(s)
      );
  }

  /** Names only, for the delete confirmation. Stable reference per master id:
   *  see rebuildLinkIndex for why that matters. */
  linkedProviderNames(masterId: number): string[] {
    return this.linkedProviderNameCache.get(masterId) ?? EMPTY_NAMES;
  }
  private linkedProviderNameCache = new Map<number, string[]>();

  linkedProviderCount(masterId: number): number {
    return this.linksByMaster.get(masterId)?.length ?? 0;
  }

  /** Full rows, so the panel can show the provider and open its rules. */
  linkedProviderEntries(masterId: number): any[] {
    return this.linksByMaster.get(masterId) ?? EMPTY_ENTRIES;
  }

  /** Linked / total for the chosen provider, which is the progress a clinic
   *  is working through. Counts entries, so same-named twins count once each. */
  get mappingLinkedCount(): number {
    return this.providerEntries.filter((d) => this.linkedMasterName(d)).length;
  }

  get mappingTotalCount(): number {
    return this.providerEntries.length;
  }

  get mappingPercent(): number {
    const total = this.mappingTotalCount;
    return total ? Math.round((this.mappingLinkedCount / total) * 100) : 0;
  }

  /** The catalogue lists only the clinic's own diagnoses: they are the ones
   *  that carry treatments and prices, and the only ones worth drilling into.
   *  Provider entries live in the mapping view instead. */
  get visibleDiagnostics(): any[] {
    const s = this.diagnosticSearch.trim().toLowerCase();
    return this.diagnostics
      .filter((d) => !d.is_System_Generated)
      .filter(
        (d) =>
          !s ||
          String(d.name ?? '')
            .toLowerCase()
            .includes(s)
      );
  }

  // -- Tree view ---------------------------------------------------------

  setDiagnosticsView(view: 'list' | 'tree') {
    this.diagnosticsView = view;
    // Opening the tree with something already selected should show it, not hide
    // it inside a closed group.
    if (view === 'tree' && this.selectedDiagnostic) {
      this.revealDiagnostic(this.selectedDiagnostic);
    }
  }

  get diagnosticTree(): DiagnosticFamilyNode[] {
    const key = `${this.diagnosticsRevision}|${this.diagnosticSearch.trim().toLowerCase()}`;
    if (this.treeCache?.key === key) return this.treeCache.tree;
    const tree = this.buildDiagnosticTree(this.visibleDiagnostics);
    this.treeCache = { key, tree };
    return tree;
  }

  /**
   * family > subfamily > diagnosis.
   *
   * Anything with no family lands in "Others" - the catalogue predates these
   * columns, so most existing rows have neither and would otherwise vanish from
   * this view entirely. A row that HAS a family but no subfamily hangs directly
   * off the family rather than inventing a second "Others" level, which would
   * only add a click.
   */
  private buildDiagnosticTree(items: any[]): DiagnosticFamilyNode[] {
    const othersLabel = this.translate.instant('DIAG_TREE.OTHERS');
    const families = new Map<string, DiagnosticFamilyNode>();

    for (const d of items) {
      const familyName = String(d.family ?? '').trim();
      const familyKey = familyName ? `f:${familyName.toLowerCase()}` : 'f:\u0000others';
      let family = families.get(familyKey);
      if (!family) {
        family = {
          key: familyKey,
          label: familyName || othersLabel,
          isOthers: !familyName,
          direct: [],
          subfamilies: [],
          count: 0,
        };
        families.set(familyKey, family);
      }
      family.count++;

      const subName = String(d.subfamily ?? '').trim();
      if (!subName) {
        family.direct.push(d);
        continue;
      }
      const subKey = `${familyKey}|s:${subName.toLowerCase()}`;
      let sub = family.subfamilies.find((x) => x.key === subKey);
      if (!sub) {
        sub = { key: subKey, label: subName, items: [] };
        family.subfamilies.push(sub);
      }
      sub.items.push(d);
    }

    const byName = (a: any, b: any) =>
      String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, { sensitivity: 'base' });

    const out = [...families.values()];
    for (const f of out) {
      f.direct.sort(byName);
      f.subfamilies.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
      for (const sub of f.subfamilies) sub.items.sort(byName);
    }
    // "Others" is a leftovers bucket, so it sorts last whatever it is called.
    out.sort((a, b) => {
      if (a.isOthers !== b.isOthers) return a.isOthers ? 1 : -1;
      return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
    });
    return out;
  }

  /** A search filters the leaves, so the groups must open or the results are
   *  invisible and the search looks broken. */
  isGroupOpen(key: string): boolean {
    return !!this.diagnosticSearch.trim() || this.openGroups.has(key);
  }

  toggleGroup(key: string) {
    if (this.openGroups.has(key)) this.openGroups.delete(key);
    else this.openGroups.add(key);
  }

  get allGroupsOpen(): boolean {
    const tree = this.diagnosticTree;
    if (!tree.length) return false;
    return tree.every(
      (f) => this.openGroups.has(f.key) && f.subfamilies.every((s) => this.openGroups.has(s.key))
    );
  }

  toggleAllGroups() {
    if (this.allGroupsOpen) {
      this.openGroups.clear();
      return;
    }
    for (const f of this.diagnosticTree) {
      this.openGroups.add(f.key);
      for (const s of f.subfamilies) this.openGroups.add(s.key);
    }
  }

  /** Open the path down to one diagnosis so it is actually on screen. */
  private revealDiagnostic(item: any) {
    for (const f of this.diagnosticTree) {
      if (f.direct.some((d) => d.id === item.id)) {
        this.openGroups.add(f.key);
        return;
      }
      for (const s of f.subfamilies) {
        if (s.items.some((d) => d.id === item.id)) {
          this.openGroups.add(f.key);
          this.openGroups.add(s.key);
          return;
        }
      }
    }
  }

  get visibleTreatments(): any[] {
    const s = this.treatmentSearch.trim().toLowerCase();
    return this.treatments
      .filter(this.providerVisible)
      .filter((t) => !s || String(t.name ?? '').toLowerCase().includes(s));
  }

  get visibleProcedures(): any[] {
    const s = this.procedureSearch.trim().toLowerCase();
    if (!s) return this.procedures;
    return this.procedures.filter((p) =>
      String(p.procedure_name ?? p.description ?? '').toLowerCase().includes(s)
    );
  }

  isDioItem(item: any): boolean {
    return !!item?.dio_ontology_rule_id;
  }

  providerLogo(item: any): string {
    return this.isDioItem(item) ? 'assets/images/logo_dio_icon.png' : 'assets/images/dignocat.png';
  }

  providerTitle(item: any): string {
    return this.translate.instant('ai_powered_by', { provider: this.isDioItem(item) ? 'Dio' : 'Diagnocat' });
  }

  canShowRules(item: any): boolean {
    return this.isDioItem(item) ? this.hasDioCreds : this.hasDiagnocatCreds;
  }

  selectedDiagnostic: any = null;
  selectedTreatment: any = null;

  loadingDiagnostics = false;
  loadingTreatments = false;
  loadingProcedures = false;

  // Modal State
  showEditModal = false;
  showProcedureModal = false;
  showDeleteModal = false;
  showRulesModal = false;
  showImportModal = false;
  showImportDiagnosticsModal = false;
  showLinkTreatmentModal = false;
  showProcedureSettingsModal = false;
  isEditingRules = false;

  modalType: 'diagnostic' | 'treatment' | 'procedure' = 'diagnostic';
  selectedItemForModal: any = null;
  selectedRules: any = null;
  editingRulesData: any = {};

  // Ontology Options for Dropdowns
  private formatOption = (v: string) => ({
    label: v.replace(/_/g, ' ').toUpperCase(),
    value: v,
  });

  domains = Ontology.DIAGNOCAT_DOMAINS.map(this.formatOption);
  crownStates = Ontology.TOOTH_CROWN_STATES.map(this.formatOption);
  rootStates = Ontology.TOOTH_ROOT_STATES.map(this.formatOption);
  renderAssets = Ontology.RENDER_ASSET_TYPES.map(this.formatOption);
  approvals = Ontology.RECOMMENDED_APPROVAL_TYPES.map(this.formatOption);
  severities = Ontology.SEVERITY_LEVELS.map(this.formatOption);
  colorRules = Ontology.COLOR_RULES.map(this.formatOption);

  clinicalRules = [
    { key: 'domain', label: 'dtp_rule_domain', options: this.domains, icon: 'pi-tag', category: 'General' },
    {
      key: 'priority_rank',
      label: 'priority_rank_label',
      icon: 'pi-sort-amount-up',
      category: 'General',
    },
    {
      key: 'default_severity',
      label: 'SEVERITY',
      options: this.severities,
      icon: 'pi-exclamation-triangle',
      category: 'General',
    },

    {
      key: 'crown_state',
      label: 'crown_state_label',
      options: this.crownStates,
      icon: 'pi-box',
      category: 'Anatomy',
    },
    {
      key: 'root_state',
      label: 'dtp_rule_root_state',
      options: this.rootStates,
      icon: 'pi-map',
      category: 'Anatomy',
    },
    {
      key: 'render_asset',
      label: 'dtp_rule_render_asset',
      options: this.renderAssets,
      icon: 'pi-image',
      category: 'Anatomy',
    },

    {
      key: 'color_rule',
      label: 'color_rule_label',
      options: this.colorRules,
      icon: 'pi-palette',
      category: 'Diagnosis',
    },
    {
      key: 'recommended_approval',
      label: 'recommended_approval_label',
      options: this.approvals,
      icon: 'pi-thumbs-up',
      category: 'Diagnosis',
    },

    {
      key: 'treatment_relevant',
      label: 'treatment_relevant_label',
      icon: 'pi-briefcase',
      isBool: true,
      category: 'Relevance',
    },
    {
      key: 'monitoring_relevant',
      label: 'monitoring_relevant_label',
      icon: 'pi-chart-line',
      isBool: true,
      category: 'Relevance',
    },
  ];

  // crown_state / root_state / render_asset are Diagnocat's odontogram-rendering
  // spec and mean nothing for Dio rules.
  private readonly diagnocatOnlyRuleKeys = ['crown_state', 'root_state', 'render_asset'];

  getRulesByCategory(category: string) {
    return this.clinicalRules.filter(
      (r) =>
        r.category === category &&
        (this.selectedRulesProvider !== 'dio' || !this.diagnocatOnlyRuleKeys.includes(r.key))
    );
  }

  async ngOnInit() {
    this.store.select(selectBranchId).subscribe((id) => {
      this.branchId = id;
      if (id) {
        this.loadDiagnostics();
        // Dio is configured per BRANCH (Diagnocat per company) - see docs/DIO_INTEGRATION.md
        this.dioService
          .isBranchConfigured(id)
          .pipe(take(1))
          .subscribe((configured) => {
            this.hasDioCreds = configured;
            this.settleMappingProvider();
            this.cdr.detectChanges();
          });
      }
    });

    this.translate.onLangChange.subscribe(() => {
      this.initProcedureMenu();
    });
    this.initProcedureMenu();

    // Check Diagnocat credentials to conditionally show bulk upload
    this.store
      .select(selectCompanyId)
      .pipe(take(1))
      .subscribe((companyId) => {
        if (companyId) {
          this.financialService
            .getCompanyDetails(companyId)
            .pipe(take(1))
            .subscribe({
              next: (company) => {
                this.hasDiagnocatCreds = !!(company?.diagnocat_user && company?.diagnocat_pass);
                this.settleMappingProvider();
                this.cdr.detectChanges();
              },
              error: () => {
                this.hasDiagnocatCreds = false;
              },
            });
        }
      });
  }

  /** Provider entry id -> its link row for this branch. */
  diagnosisLinks = new Map<number, any>();
  showLinkDiagnosisModal = false;
  diagnosticToLink: any = null;

  /** The branch's own diagnoses by id, for resolving a link to its name.
   *  Kept separate from `diagnostics` because that list is deduplicated by
   *  name in favour of the system row, so a linked clinic diagnosis sharing a
   *  provider entry's name would not be found there. */
  private ownDiagnosticNames = new Map<number, string>();

  /** Name of the clinic diagnosis a provider entry points at, if linked. */
  linkedMasterName(item: any): string | null {
    const link = this.diagnosisLinks.get(item?.id);
    if (!link) return null;
    return this.ownDiagnosticNames.get(link.master_diagnostic_id) ?? null;
  }

  linkedMasterId(item: any): number | null {
    return this.diagnosisLinks.get(item?.id)?.master_diagnostic_id ?? null;
  }

  onLinkDiagnosis(item: any) {
    if (!this.canEdit) return;
    // Must be set: the shared post-save refresh dispatches on modalType, so
    // leaving the previous modal's value here reloads the wrong list and the
    // new link never appears.
    this.modalType = 'diagnostic';
    this.diagnosticToLink = item;
    this.showLinkDiagnosisModal = true;
  }

  private async loadDiagnosisLinks() {
    if (!this.branchId) return;
    try {
      const [links, own] = await Promise.all([
        firstValueFrom(this.settingsService.getDiagnosisLinks(this.branchId)),
        firstValueFrom(this.settingsService.getBranchOwnDiagnostics(this.branchId)),
      ]);
      this.diagnosisLinks = links;
      this.ownDiagnosticNames = new Map((own ?? []).map((d: any) => [d.id, d.name]));
    } catch (err) {
      // A clinic with no links yet is the normal case; never block the list.
      console.error('Error loading diagnosis links:', err);
      this.diagnosisLinks = new Map();
      this.ownDiagnosticNames = new Map();
    }
    this.rebuildLinkIndex();
  }

  /**
   * Reverse index: clinic diagnosis id -> the provider entries pointing at it,
   * deduplicated by name. Built once whenever the links or the catalogue
   * change, because the template asks for it per row on every change-detection
   * pass, and returning a fresh array each time also re-triggered the edit
   * modal's ngOnChanges continuously.
   */
  private linksByMaster = new Map<number, any[]>();

  private rebuildLinkIndex() {
    const byId = new Map<number, any>(this.diagnostics.map((d: any) => [d.id, d]));
    const index = new Map<number, Map<string, any>>();
    for (const link of this.diagnosisLinks.values()) {
      const entry = byId.get(link.provider_diagnostic_id);
      const name = String(entry?.name ?? '').trim();
      if (!entry || !name) continue;
      if (!index.has(link.master_diagnostic_id)) index.set(link.master_diagnostic_id, new Map());
      index.get(link.master_diagnostic_id)!.set(name.toLowerCase(), entry);
    }
    this.linksByMaster = new Map(
      [...index].map(([masterId, entries]) => [
        masterId,
        [...entries.values()].sort((a, b) => String(a.name).localeCompare(String(b.name))),
      ])
    );
    this.linkedProviderNameCache = new Map(
      [...this.linksByMaster].map(([masterId, entries]) => [
        masterId,
        entries.map((e) => String(e.name)),
      ])
    );
  }

  async loadDiagnostics() {
    if (!this.branchId) return;
    this.loadingDiagnostics = true;
    try {
      await this.loadDiagnosisLinks();
      const data = await firstValueFrom(this.settingsService.getDiagnostics(this.branchId));
      this.diagnostics = (data || []).map((d: any) => ({
        ...d,
        name:
          d.is_System_Generated && d.code
            ? `${d.name} - ${d.code}`
            : d.diagnostic_formatted_name || d.name,
      }));
      // Links load first, so the index must be rebuilt here: it resolves link
      // rows against the catalogue, which did not exist a moment ago.
      this.rebuildLinkIndex();
      // Names are rewritten just above, so the grouped view must be regrouped.
      this.diagnosticsRevision++;
      this.treatments = [];
      this.procedures = [];
      this.selectedDiagnostic = null;
      this.selectedTreatment = null;
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loadingDiagnostics = false;
      this.cdr.detectChanges();
    }
  }

  async selectDiagnostic(diagnostic: any) {
    if (this.selectedDiagnostic?.id === diagnostic.id) {
      this.selectedDiagnostic = null;
      this.treatments = [];
      this.selectedTreatment = null;
      this.procedures = [];
      this.treatmentsScope = 'diagnostic';
      this.treatmentSearch = '';
      this.procedureSearch = '';
      this.mobilePane = 'diagnostics';
      return;
    }

    this.selectedDiagnostic = diagnostic;
    this.selectedTreatment = null;
    this.procedures = [];
    // Picking a diagnosis is itself the instruction to scope back to it.
    this.treatmentsScope = 'diagnostic';
    // A filter left over from the previous diagnosis would silently hide the new
    // list and read as "this diagnosis has no treatments".
    this.treatmentSearch = '';
    this.procedureSearch = '';
    this.mobilePane = 'treatments';
    await this.loadTreatments(diagnostic.id);
  }

  /**
   * Show every treatment in the branch.
   *
   * The diagnosis is cleared because the two are mutually exclusive: leaving one
   * highlighted while listing treatments it has nothing to do with is exactly
   * the confusion this is meant to remove. Add / Add existing stay disabled,
   * since both attach a treatment TO a diagnosis and there is none here.
   */
  async showAllTreatments() {
    if (this.treatmentsScope === 'all') return;
    this.treatmentsScope = 'all';
    this.selectedDiagnostic = null;
    this.selectedTreatment = null;
    this.procedures = [];
    this.treatmentSearch = '';
    this.procedureSearch = '';
    this.mobilePane = 'treatments';
    await this.loadAllTreatments();
  }

  private async loadAllTreatments() {
    if (!this.branchId) return;
    this.loadingTreatments = true;
    this.cdr.detectChanges();
    try {
      this.treatments = await firstValueFrom(
        this.settingsService.getAvailableTreatments(this.branchId)
      );
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loadingTreatments = false;
      this.cdr.detectChanges();
    }
  }

  async loadTreatments(diagnosticId: number) {
    if (!this.branchId) return;
    this.loadingTreatments = true;
    try {
      this.treatments = await firstValueFrom(
        this.settingsService.getTreatments(this.branchId, diagnosticId)
      );
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loadingTreatments = false;
      this.cdr.detectChanges();
    }
  }

  async selectTreatment(treatment: any) {
    if (this.selectedTreatment?.id === treatment.id) {
      this.selectedTreatment = null;
      this.procedures = [];
      this.procedureSearch = '';
      this.mobilePane = 'treatments';
      return;
    }

    this.selectedTreatment = treatment;
    this.procedureSearch = '';
    this.mobilePane = 'procedures';
    await this.loadProcedures(treatment.id);
  }

  async loadProcedures(treatmentId: number) {
    this.loadingProcedures = true;
    try {
      this.procedures = await firstValueFrom(
        this.settingsService.getProceduresByTreatment(treatmentId)
      );
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_loading_data'));
    } finally {
      this.loadingProcedures = false;
      this.cdr.detectChanges();
    }
  }

  onAdd(type: 'diagnostic' | 'treatment' | 'procedure') {
    if (!this.canCreate) return;
    this.modalType = type;
    this.selectedItemForModal = null;
    if (type === 'procedure') {
      this.showProcedureModal = true;
    } else {
      this.showEditModal = true;
    }
  }

  onLinkTreatment() {
    if (!this.canEdit) return;
    this.modalType = 'treatment';
    this.showLinkTreatmentModal = true;
  }

  onEdit(item: any, type: 'diagnostic' | 'treatment' | 'procedure') {
    if (!this.canEdit) return;
    this.modalType = type;
    this.selectedItemForModal = item;
    this.showEditModal = true;
  }

  onDelete(item: any, type: 'procedure') {
    if (!this.canDelete) return;
    this.modalType = type;
    this.selectedItemForModal = item;
    this.showDeleteModal = true;
    this.cdr.detectChanges();
  }

  onShowProcedureSettings(item: any) {
    if (!this.canEdit) return;
    this.modalType = 'procedure';
    this.selectedItemForModal = item;
    this.showProcedureSettingsModal = true;
    this.cdr.detectChanges();
  }

  procedureMenuItems: MenuItem[] = [];

  initProcedureMenu() {
    this.procedureMenuItems = [
      {
        label: this.translate.instant('action_settings'),
        icon: 'pi pi-cog',
        visible: this.canEdit,
        command: () => this.onShowProcedureSettings(this.selectedItemForModal),
      },
      {
        label: this.translate.instant('action_delete'),
        icon: 'pi pi-trash',
        visible: this.canDelete,
        command: () => this.onDelete(this.selectedItemForModal, 'procedure'),
      },
    ];
  }

  selectedRulesProvider: 'diagnocat' | 'dio' = 'diagnocat';

  onShowRules(item: any) {
    this.selectedItemForModal = item;
    this.selectedRulesProvider = this.isDioItem(item) ? 'dio' : 'diagnocat';
    this.selectedRules = {
      ...(this.isDioItem(item) ? item.dio_ontology_rules : item.diagnocat_ontology_rules),
    };
    this.isEditingRules = false;
    this.showRulesModal = true;
    this.cdr.detectChanges();
  }

  onEditRules() {
    if (!this.canEdit) return;
    this.editingRulesData = { ...this.selectedRules };
    this.isEditingRules = true;
    this.cdr.detectChanges();
  }

  private authService = inject(AuthService);

  async saveRules() {
    if (!this.canEdit) return;
    if (!this.branchId || !this.selectedRules?.id) return;
    const companyId = this.authService.userProfile?.['company_id'];
    if (!companyId) return;

    try {
      const dataToSave: any = {
        color_rule: this.editingRulesData.color_rule,
        recommended_approval: this.editingRulesData.recommended_approval,
        treatment_relevant: this.editingRulesData.treatment_relevant,
        monitoring_relevant: this.editingRulesData.monitoring_relevant,
        priority_rank: this.editingRulesData.priority_rank,
        default_severity: this.editingRulesData.default_severity,
        domain: this.editingRulesData.domain,
      };
      if (this.selectedRulesProvider !== 'dio') {
        dataToSave.crown_state = this.editingRulesData.crown_state;
        dataToSave.root_state = this.editingRulesData.root_state;
        dataToSave.render_asset = this.editingRulesData.render_asset;
      }

      await firstValueFrom(
        this.selectedRulesProvider === 'dio'
          ? this.settingsService.upsertDioOntologyOverride(
              companyId,
              this.selectedRules.id,
              dataToSave
            )
          : this.settingsService.upsertOntologyOverride(
              companyId,
              this.selectedRules.id,
              dataToSave
            )
      );
      toast.success(this.translate.instant('save_success'));

      // Update local state
      this.selectedRules = { ...this.selectedRules, ...dataToSave };
      this.isEditingRules = false;
      this.cdr.detectChanges();
    } catch (err) {
      console.error(err);
      toast.error(this.translate.instant('error_saving_data'));
    }
  }

  cancelEditRules() {
    this.isEditingRules = false;
    this.cdr.detectChanges();
  }

  formatValue(val: any): string {
    if (val === null || val === undefined) return 'N/A';
    if (typeof val === 'boolean') return val ? 'Yes' : 'No';
    if (typeof val === 'string') {
      return val.replace(/_/g, ' ').toUpperCase();
    }
    return String(val);
  }

  getColorCode(val: string): string {
    const v = val?.toUpperCase() || '';
    const map: { [key: string]: string } = {
      RED: '#ef4444',
      ORANGE: '#f59e0b',
      BLUE: '#3b82f6',
      WHITE: '#ffffff',
      GRAY: '#94a3b8',
      GREEN: '#10b981',
      DECIDE: '#f59e0b',
      X_RED: '#991b1b',
    };
    return map[v] || 'transparent';
  }

  closeRulesModal() {
    this.showRulesModal = false;
    this.selectedRules = null;
    this.cdr.detectChanges();
  }

  getProcedureId = (p: any) => p.id;

  get excludedProcedureIds() {
    return this.procedures.map((p) => p.id);
  }

  get excludedTreatmentIds() {
    return this.treatments.map((t) => t.id);
  }

  onModalSaved() {
    this.closeModals();
    this.refreshAfterChange();
  }

  onModalDeleted() {
    this.closeModals();
    this.refreshAfterChange();
  }

  closeModals() {
    this.showLinkDiagnosisModal = false;
    this.diagnosticToLink = null;
    this.showEditModal = false;
    this.showProcedureModal = false;
    this.showDeleteModal = false;
    this.showLinkTreatmentModal = false;
    this.selectedItemForModal = null;
  }

  refreshAfterChange() {
    if (this.modalType === 'diagnostic') {
      this.loadDiagnostics();
    } else if (this.modalType === 'treatment') {
      this.loadTreatments(this.selectedDiagnostic.id);
    } else if (this.modalType === 'procedure') {
      this.loadProcedures(this.selectedTreatment.id);
    }
  }

  onOpenImport() {
    if (!this.canCreate) return;
    this.showImportModal = true;
    this.cdr.detectChanges();
  }

  onCloseImport() {
    this.showImportModal = false;
    this.cdr.detectChanges();
  }

  onImportSaved() {
    this.showImportModal = false;
    this.loadDiagnostics();
    this.cdr.detectChanges();
  }

  onOpenImportDiagnostics() {
    if (!this.canCreate) return;
    this.showImportDiagnosticsModal = true;
    this.cdr.detectChanges();
  }

  /**
   * The modal stays open on its own summary after importing - it is the only
   * place the real inserted/updated counts are shown - so this refreshes the
   * catalogue behind it without closing it.
   */
  onDiagnosticsImported() {
    this.loadDiagnostics();
    this.cdr.detectChanges();
  }

  onCloseImportDiagnostics() {
    this.showImportDiagnosticsModal = false;
    this.cdr.detectChanges();
  }
}
